import { FileText, Download } from "lucide-react";
import type { ToolWidgetProps } from "./index";

interface Document {
  id: number;
  filename: string;
  file_url: string;
  byte_size: number;
  content_type: string;
  content_preview?: string;
  created_at: string;
}

interface SearchDocumentsData {
  results: Document[];
  count: number;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
}

export function SearchDocumentsWidget({
  data,
  count,
}: ToolWidgetProps<SearchDocumentsData>) {
  const { results = [] } = data || {};

  if (!results.length) {
    return (
      <div className="text-sm text-slate-500 italic">Aucun document trouvé</div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-3 py-2 font-medium text-slate-700">
              Document
            </th>
            <th className="text-left px-3 py-2 font-medium text-slate-700">
              Taille
            </th>
            <th className="text-left px-3 py-2 font-medium text-slate-700">
              Aperçu
            </th>
            <th className="w-16"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {results.map((doc) => (
            <tr key={doc.id} className="hover:bg-slate-50">
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="font-medium text-slate-800 truncate">
                    {doc.filename}
                  </span>
                </div>
              </td>
              <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                {formatBytes(doc.byte_size)}
              </td>
              <td className="px-3 py-2">
                {doc.content_preview && (
                  <div className="text-xs text-slate-500 truncate max-w-md">
                    {doc.content_preview.substring(0, 100)}...
                  </div>
                )}
              </td>
              <td className="px-3 py-2">
                <a
                  href={doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-default-600 hover:text-default-700"
                >
                  <Download className="w-4 h-4" />
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {count && count > results.length && (
        <div className="bg-slate-50 px-3 py-2 text-xs text-slate-400 text-center border-t border-slate-200">
          + {count - results.length} autres documents
        </div>
      )}
    </div>
  );
}
